import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Suspense, useState, useEffect, useCallback, useMemo } from 'react'
import Sidebar from './Sidebar.js'
import Topbar from './Topbar.js'
import JobsPanel from './JobsPanel.js'
import NewDubDrawer from './NewDubDrawer.js'
import PlayerPanel from './PlayerPanel.js'
import FullScreenPlayer from './FullScreenPlayer.js'
import NotificationToast from './NotificationToast.js'
import NotificationDrawer from './NotificationDrawer.js'
import CaptureEngine from './CaptureEngine.js'
import { PlayerContext, usePlayerProvider } from '../hooks/usePlayer.js'
import { CaptureContext } from '../hooks/useCapture.js'
import type { CaptureInfo } from './CaptureModal.js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchNotifications, fetchJobs } from '../lib/api.js'
import { useAuth } from '../hooks/useAuth.js'
import { useTheme } from '../hooks/useTheme.js'
import { useTranslation } from 'react-i18next'
import { DrawerContext } from '../hooks/useDrawer.js'

const MOBILE_NAV_ITEMS = [
  { path: '/dashboard', labelKey: 'nav.watch',     icon: 'M2 3h20v14H2zM8 21h8M12 17v4' },
  { path: '/extension', labelKey: 'nav.extension', icon: 'M12 2a10 10 0 110 20 10 10 0 010-20zM2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20' },
  { path: '/usage',     labelKey: 'nav.usage',     icon: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z' },
]

export default function AppShell() {
  const { theme, toggleTheme: onToggleTheme } = useTheme()
  const { user } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()
  const nav = useNavigate()
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [mobileJobsOpen, setMobileJobsOpen] = useState(false)
  const playerProvider = usePlayerProvider()

  // ── Capture queue (lives here so CaptureEngine survives navigation) ──
  const [captureQueue, setCaptureQueue] = useState<CaptureInfo[]>([])
  const [captureProgress, setCaptureProgress] = useState({ elapsed: 0, expected: 0 })
  const [sharedStream, setSharedStream] = useState<MediaStream | null>(null)

  const activeCapture = captureQueue[0] ?? null

  const startCapture = useCallback((info: CaptureInfo) => {
    setCaptureQueue(q => [...q, info])
    setCaptureProgress({ elapsed: 0, expected: 0 })
  }, [])

  const clearCapture = useCallback(() => {
    setCaptureQueue(q => {
      const next = q.slice(1)
      if (next.length === 0) {
        // Queue empty — stop the shared stream
        sharedStream?.getTracks().forEach(t => t.stop())
        setSharedStream(null)
      }
      return next
    })
    setCaptureProgress({ elapsed: 0, expected: 0 })
    void qc.invalidateQueries({ queryKey: ['jobs'] })
  }, [sharedStream, qc])

  const captureCtx = useMemo(() => ({
    captureQueue,
    activeCapture,
    captureProgress,
    sharedStream,
    startCapture,
    clearCapture,
    setSharedStream,
  }), [captureQueue, activeCapture, captureProgress, sharedStream, startCapture, clearCapture])

  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    enabled: !!user,
    refetchInterval: 30_000,
  })

  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => fetchJobs(20),
    enabled: !!user,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? []
      const hasActive = jobs.some((j: { status: string }) => !['ready', 'failed', 'expired'].includes(j.status))
      return hasActive ? 3000 : false
    },
  })

  const unreadCount = (notifData?.notifications ?? []).filter((n: { read: boolean }) => !n.read).length
  const activeJobCount = (jobsData?.jobs ?? []).filter((j: { status: string }) => !['ready', 'failed', 'expired', 'queued'].includes(j.status)).length

  // Close mobile menus on route change
  useEffect(() => {
    setMobileNavOpen(false)
    setMobileJobsOpen(false)
  }, [location.pathname])

  // Add body class for noise overlay
  useEffect(() => {
    document.body.classList.add('app-shell-active')
    return () => document.body.classList.remove('app-shell-active')
  }, [])

  const toggleMobileNav = useCallback(() => {
    setMobileNavOpen(o => !o)
    setMobileJobsOpen(false)
  }, [])

  const toggleMobileJobs = useCallback(() => {
    setMobileJobsOpen(o => !o)
    setMobileNavOpen(false)
  }, [])

  const drawerCtx = useMemo(() => ({ openDrawer: () => setDrawerOpen(true) }), [])

  return (
    <CaptureContext.Provider value={captureCtx}>
    <DrawerContext.Provider value={drawerCtx}>
    <PlayerContext.Provider value={playerProvider.value}>
      {/* Ambient blobs */}
      <div className="ambient-blobs">
        <div className="ab ab1" />
        <div className="ab ab2" />
        <div className="ab ab3" />
      </div>

      <div className="app-shell">
        <Sidebar theme={theme} onToggleTheme={onToggleTheme} />

        <main className="app-workspace">
          <Topbar
            onNewDub={() => setDrawerOpen(true)}
            onOpenNotifications={() => setNotifOpen(o => !o)}
            unreadCount={unreadCount}
            onToggleMobileNav={toggleMobileNav}
            onToggleMobileJobs={toggleMobileJobs}
            activeJobCount={activeJobCount}
          />
          <div className="app-content">
            <Suspense fallback={null}>
              <div className="page-enter">
                <Outlet />
              </div>
            </Suspense>
          </div>
        </main>

        <JobsPanel onNewDub={() => setDrawerOpen(true)} />
      </div>

      {/* Mobile nav overlay */}
      {mobileNavOpen && <div className="mobile-overlay" onClick={() => setMobileNavOpen(false)} />}
      <div className={`mobile-nav${mobileNavOpen ? ' open' : ''}`}>
        {MOBILE_NAV_ITEMS.map(item => (
          <button
            key={item.path}
            className={`mobile-nav-item${location.pathname === item.path ? ' active' : ''}`}
            onClick={() => nav(item.path)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d={item.icon} />
            </svg>
            <span>{t(item.labelKey)}</span>
          </button>
        ))}
        <div className="mobile-nav-divider" />
        <button className="mobile-nav-item" onClick={onToggleTheme}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            {theme === 'dark'
              ? <><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></>
              : <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
            }
          </svg>
          <span>{theme === 'dark' ? t('shell.lightMode', 'Light mode') : t('shell.darkMode', 'Dark mode')}</span>
        </button>
      </div>

      {/* Mobile jobs sheet */}
      {mobileJobsOpen && <div className="mobile-overlay" onClick={() => setMobileJobsOpen(false)} />}
      <div className={`mobile-jobs-sheet${mobileJobsOpen ? ' open' : ''}`}>
        <JobsPanel onNewDub={() => { setMobileJobsOpen(false); setDrawerOpen(true) }} />
      </div>

      <NewDubDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <PlayerPanel />
      <FullScreenPlayer />
      <NotificationToast />
      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* CaptureEngine lives here (not in DashboardPage) so it survives navigation */}
      {activeCapture && (
        <CaptureEngine
          key={activeCapture.jobId}
          videoId={activeCapture.videoId}
          stream={activeCapture.stream}
          jobId={activeCapture.jobId}
          uploadUrl={activeCapture.uploadUrl}
          mixSettings={activeCapture.mixSettings}
          onProgress={(elapsed, expected) => setCaptureProgress({ elapsed, expected })}
          onComplete={() => clearCapture()}
          onError={() => clearCapture()}
        />
      )}
    </PlayerContext.Provider>
    </DrawerContext.Provider>
    </CaptureContext.Provider>
  )
}
